import type { DropdownOption } from '~/components/Dropdown';
import type { BaseEntity, LabelDetail } from '~settings/api/lib/schemas';

const SOCIAL_KEYS = [
  'facebookReference',
  'twitterReference',
  'website',
  'youtubeReference',
  'instagramReference',
] as const;

type SocialKeys = (typeof SOCIAL_KEYS)[number];

export interface LabelDetailTransformed extends LabelDetail {
  isSublabel: boolean;
  socials: [SocialKeys, string][];
  distributionGroups: NonNullable<LabelDetail['distributionGroups']>;
  labelGroups: NonNullable<LabelDetail['labelGroups']>;
}

export const transformLabelDetails = (
  labelDetail: LabelDetail,
): LabelDetailTransformed => {
  const socials = SOCIAL_KEYS.reduce((acc: [SocialKeys, string][], key) => {
    if (labelDetail[key]) {
      let value = labelDetail[key];

      if (!/^(https?:)/.test(value)) {
        value = `https://${value}`;
      }

      acc.push([key, value]);
    }

    return acc;
  }, []);

  return {
    ...labelDetail,
    isSublabel: Boolean(labelDetail.parentId),
    distributionGroups: labelDetail.distributionGroups ?? [],
    labelGroups: labelDetail.labelGroups ?? [],
    socials,
  };
};

export function toDropdownList<
  TId extends string,
  TData extends Record<string, unknown>,
>(
  list: ({ id: TId } & TData)[] = [],
  propertyOrFunctor: string | ((item: { id: TId } & TData) => string) = 'name',
): { value: TId; label: string }[] {
  return list.reduce<DropdownOption<TId>[]>((acc, next) => {
    const label =
      typeof propertyOrFunctor === 'function'
        ? propertyOrFunctor(next)
        : (next[propertyOrFunctor] as string);

    if (next.id !== undefined && label) {
      acc.push({
        value: next.id,
        label,
      });
    }

    return acc;
  }, []);
}

export function fromDropdownList<T extends string>(
  list: DropdownOption<T>[] = [],
): BaseEntity[] {
  return list.map((item) => ({
    id: item.value,
    name: item.label,
  }));
}
